import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthedFetch, parseJsonOrThrow, throwIfNotOk } from './client';
import { ListSchedulesResponse, ScheduleDoc } from '../domain';
import { z } from 'zod';

// GET /schedules is added by Task 14; hooks ship now so frontend
// tasks aren't blocked, but `useSchedulesQuery` will 404 until then.
export function useSchedulesQuery() {
  const fetcher = useAuthedFetch();
  return useQuery({
    queryKey: ['schedules'],
    queryFn: async () => {
      const res = await fetcher('/schedules');
      const parsed = await parseJsonOrThrow(res, 'GET /schedules', (raw) => ListSchedulesResponse.parse(raw));
      return z.array(ScheduleDoc).parse(parsed.schedules);
    },
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60_000,
  });
}

export function usePutScheduleMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (doc: ScheduleDoc) => {
      const res = await fetcher(`/schedules/${doc.id}`, { method: 'PUT', body: JSON.stringify(doc) });
      return parseJsonOrThrow(res, `PUT /schedules/${doc.id}`, (raw) => ScheduleDoc.parse(raw));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['state'] });
    },
  });
}

export function useDeleteScheduleMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetcher(`/schedules/${id}`, { method: 'DELETE' });
      await throwIfNotOk(res, `DELETE /schedules/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['state'] });
    },
  });
}
